import { getClient } from "Globals/api";
import gql from "graphql-tag.macro";

export const updateV3Project = async (input) => {
  const client = await getClient("projectcontext");

  const { data } = await client.mutate({
    mutation: gql`
      mutation updateProject($input: ProjectInput!) {
        updateProjectAdv(input: $input)
      }
    `,
    variables: { input },
    context: {
      headers: {
        "x-scope-instance": window.app.Duchess.currentInstance._id || "",
        "x-scope-project": window.app.Duchess.currentProject._id || "",
      },
    },
  });

  return data.updateProjectAdv;
};

/**
 * Saves a project with the provided form values.
 *
 * Validates that the project title is unique (excluding the current project by ID).
 * Throws an error if a project with the same title already exists.
 *
 * @async
 * @param {Object} formValues - The values of the project form.
 * @param {string|number} [formValues.id] - The unique identifier of the project.
 * @param {string} formValues.title - The title of the project.
 * @param {string} formValues.organisation - The organisation associated with the project.
 * @throws {Error} If a project with the same title already exists.
 * @returns {Promise<void>} Resolves when the project is successfully saved.
 */
export const saveProject = async (formValues = {}) => {
  // Validate form values
  const projectList = await getProjectList();
  if (
    projectList.some(
      (p) => p.title === formValues.title && p.id !== formValues.id
    )
  ) {
    throw new Error("Project with this name already exists");
  }
  const res = await new Promise((resolve, reject) => {
    window.app.Duchess.modifyObject(
      "Project",
      {
        ID: formValues.id,
        title: formValues.title,
        organisation: formValues.organisation,
      },
      (res) => {
        return res.success ? resolve(res) : reject(res);
      }
    );
  });
  if (!res.success) {
    throw new Error(res.message || "Failed to save project");
  }
  await updateV3Project({
    legacyId: formValues.id,
    name: formValues.title,
    _organisation: formValues.organisation,
  });
};

/**
 * Deletes a V3 project by its legacy ID.
 *
 * @async
 * @function
 * @param {number|string} legacyId - The legacy ID of the project to delete.
 * @returns {Promise<any>} The result of the deletedProjects mutation.
 */
export const deleteV3Project = async (legacyId) => {
  const client = await getClient("projectcontext");
  const numericId = parseInt(legacyId, 10); // ensure it's an integer

  const { data } = await client.mutate({
    mutation: gql`
      mutation deleteProjects($legacyIds: [Int!]!) {
        deletedProjects(legacyIds: $legacyIds)
      }
    `,
    variables: { legacyIds: [numericId] },
    context: {
      headers: {
        "x-scope-instance": window.app.Duchess.currentInstance._id || "",
        "x-scope-project": window.app.Duchess.currentProject._id || ""
      },
    },
  });

  return data.deletedProjects;
};

/**
 * Fetches the list of projects (V3) from the GraphQL API.
 *
 * @async
 * @function getV3ProjectList
 */
export const getV3ProjectList = async () => {
  const client = await getClient("projectcontext");
  const { data } = await client.query({
    query: gql`
      query {
        projectList {
          _id
          name
          _organisation
          legacyId
          keycloakId
        }
      }
    `,
    fetchPolicy: "no-cache",
    context: {
      headers: {
        "x-scope-instance": window.app.Duchess.currentInstance._id || "",
        "x-scope-project": window.app.Duchess.currentProject._id || "",
      },
    },
  });
  return data.projectList || [];
};

/**
 * Retrieves and merges project lists from both V2 (legacy Duchess) and V3 (GraphQL) sources.
 *
 * - Fetches V3 projects via GraphQL (`getV3ProjectList`).
 * - Fetches V2 projects via Duchess legacy API.
 * - Merges both lists by legacyId (V2 `id` == V3 `legacyId`).
 * - V2 project fields take priority in the merged result.
 * - Ensures V2 `title` and `organisation` are used if available.
 * - Adds flags: `hasV2`, `hasV3`, `hasKeycloak`, and computes `safeToDelete`.
 *
 * @async
 * @function
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of merged project objects with the following properties:
 *   @property {string} id - The project identifier (V2 `id` or V3 `legacyId`).
 *   @property {string} [title] - The project title (V2 `title` or V3 `name`).
 *   @property {string} [organisation] - The project organisation (V2 `organisation` or V3 `_organisation`).
 *   @property {boolean} [safeToDelete] - Whether the project is safe to delete.
 *   @property {boolean} hasV2 - Whether the project exists in V2.
 *   @property {boolean} hasV3 - Whether the project exists in V3.
 *   @property {boolean} hasKeycloak - Whether the V3 project has a Keycloak ID.
 *   @property {...any} [other] - Other properties from V2 and V3 project objects.
 */
export const getProjectList = async () => {
  // Get V3 projects via GraphQL
  const v3Projects = await getV3ProjectList(); // from GraphQL
  const v3Map = new Map(v3Projects.map((p) => [String(p.legacyId), p]));

  // Get V2 projects via Duchess (legacy)
  const v2Response = await new Promise((resolve, reject) => {
    window.app.Duchess.sendRequest(
      "Project:objectCustomProject",
      {
        ID: window.app.Duchess.getCookie("CurrentProjectID"),
        method: "getAllProjects",
        sessionID: window.app.Duchess.getCookie("SESSION_ID"),
        usedInProject: window.app.Duchess.getCookie("CurrentProjectID"),
        v3ProjectId: window.app.Duchess.currentInstance._id,
      },
      (res) => {
        return res.success ? resolve(res) : reject(res);
      }
    );
  });

  const v2List = v2Response?.list || [];
  const v2Map = new Map(v2List.map((p) => [String(p.id), p]));

  // Merge both V2 and V3 by legacyId (project.id in V2 == legacyId in V3)
  const allIds = new Set([
    ...Array.from(v2Map.keys()),
    ...Array.from(v3Map.keys()),
  ]);

  const mergedList = Array.from(allIds).map((id) => {
    const v2Project = v2Map.get(id) || {};
    const v3Project = v3Map.get(id) || {};

    const hasV2 = v2Map.has(id);
    const hasV3 = v3Map.has(id);
    const hasKeycloak = v3Project.keycloakId != null;
    const safeToDelete =
      !v2Project?.id ||
      (v2Project.safeToDelete === true && (!hasV3 || !hasKeycloak));

    return {
      id,
      ...v3Project,
      ...v2Project, // v2 takes priority in spread
      // Ensure V2 title/organisation are used if available
      title: v2Project.title || v3Project.name,
      organisation: v2Project.organisation || v3Project._organisation,
      safeToDelete,
      hasV2,
      hasV3,
      hasKeycloak,
    };
  });

  return mergedList;
};

/**
 * Deletes a project by sending a request to the MDv2 BE and, if successful, deletes the V3 project as well.
 *
 * @async
 * @function deleteProject
 * @param {Object} project - The project object to be deleted.
 * @param {string|number} project.id - The unique identifier of the project.
 * @param {boolean} [project.hasV2] - Indicates if the project exists in V2.
 * @param {boolean} [project.hasV3] - Indicates if the project exists in V3.
 * @returns {Promise<Object>} The response from the backend if successful, or the error object if the operation fails.
 */
export const deleteProject = async (project) => {
  let res;
  try {
    res = await new Promise((reso, rej) => {
      window.app.Duchess.sendRequest(
        "Project:objectCustomProject",
        {
          method: "deleteProject",
          ID: project.id,
          sessionID: window.app.Duchess.getCookie("SESSION_ID"),
          usedInProject: project.id,
          v3ProjectId: window.app.Duchess.currentInstance._id,
        },
        async (res) => {
          return res.success ? reso(res) : rej(res);
        }
      );
    });
    await deleteV3Project(project.id);
    return res;
  } catch (error) {
    if (!project?.hasV2 && project?.hasV3) {
      return await deleteV3Project(project.id);
    }
    return error;
  }
};
